/**
 * EvidenceBundleBlock - Display sources with reliability scores and excerpts
 */

import React, { useState } from 'react'
import { EvidenceBundleBlock as EvidenceBundleBlockType } from '../../../../lib/cockpit/schemas'

interface EvidenceBundleBlockProps {
  data: EvidenceBundleBlockType['data']
  blockId: string
  compact?: boolean
}

function ReliabilityBadge({ score }: { score: number }) {
  const percentage = Math.round(score * 100)
  const color = score >= 0.8 ? 'text-green-600 bg-green-50 dark:bg-green-900/30' 
    : score >= 0.6 ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'
    : score >= 0.4 ? 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30'
    : 'text-red-600 bg-red-50 dark:bg-red-900/30'
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${color}`}>
      {percentage}%
    </span>
  )
}

function EvidenceBundleBlock({ data, blockId, compact }: EvidenceBundleBlockProps) {
  const [expandedSource, setExpandedSource] = useState<string | null>(null)
  const [pinnedSources, setPinnedSources] = useState<Set<string>>(() => 
    new Set(data.sources.filter((s: any) => s.pinned).map((s: any) => s.id))
  )

  const togglePin = (sourceId: string) => {
    setPinnedSources(prev => {
      const next = new Set(prev)
      if (next.has(sourceId)) {
        next.delete(sourceId)
      } else {
        next.add(sourceId)
      }
      return next
    })
  }

  // Sort: pinned first, then by reliability
  const sortedSources = [...data.sources].sort((a, b) => {
    if (pinnedSources.has(a.id) && !pinnedSources.has(b.id)) return -1
    if (!pinnedSources.has(a.id) && pinnedSources.has(b.id)) return 1
    return b.reliability_score - a.reliability_score
  })

  if (compact) {
    return (
      <div className="space-y-1">
        {sortedSources.slice(0, 5).map(source => (
          <div key={source.id} className="flex items-center gap-2 text-sm">
            <ReliabilityBadge score={source.reliability_score} />
            <span className="text-gray-700 dark:text-gray-300 truncate">{source.title}</span>
          </div>
        ))}
        {sortedSources.length > 5 && (
          <div className="text-xs text-gray-500">+{sortedSources.length - 5} more sources</div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      {data.show_reliability_scores && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            {sortedSources.length} source{sortedSources.length !== 1 ? 's' : ''}
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            Avg reliability: {Math.round((sortedSources.reduce((sum, s) => sum + s.reliability_score, 0) / sortedSources.length) * 100)}%
          </span>
        </div>
      )}

      {/* Sources */}
      {sortedSources.map(source => {
        const isExpanded = expandedSource === source.id
        const isPinned = pinnedSources.has(source.id)
        
        return (
          <div
            key={source.id}
            className={`border rounded-lg overflow-hidden ${
              isPinned 
                ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10' 
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            }`}
          >
            {/* Source header */}
            <div className="p-3 flex items-start gap-3">
              <button
                onClick={() => setExpandedSource(isExpanded ? null : source.id)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2 mb-1">
                  {data.show_reliability_scores && (
                    <ReliabilityBadge score={source.reliability_score} />
                  )}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {source.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  {source.publisher && <span>{source.publisher}</span>}
                  {source.date && <span>• {source.date}</span>}
                </div>
              </button>
              
              {/* Pin button */}
              <button
                onClick={() => togglePin(source.id)}
                className={`p-1.5 rounded ${
                  isPinned 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
                title={isPinned ? 'Unpin source' : 'Pin source'}
              >
                <svg className="w-4 h-4" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>

              {/* External link */}
              {source.url && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-700">
                {/* Reliability rationale */}
                <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-800/50 rounded text-xs text-gray-600 dark:text-gray-400">
                  <strong>Reliability:</strong> {source.reliability_rationale}
                </div>

                {/* Excerpts */}
                {source.excerpts && source.excerpts.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Excerpts:</div>
                    {source.excerpts.map((excerpt: any) => (
                      <div key={excerpt.id} className="pl-3 border-l-2 border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                          "{excerpt.text}"
                        </p>
                        {excerpt.page && (
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            Page {excerpt.page}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default EvidenceBundleBlock



